import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, RefreshCw, Trash2, UserPlus, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { inquilinosApi } from '../../../lib/api';

export const AccesoInquilinoSection = ({ contrato, unidades }) => {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [acting, setActing] = useState(false);
  const [credencialReciente, setCredencialReciente] = useState(null); // {username, password}

  const contratoId = contrato?.contrato_id;

  // Detectar si aplica (algún unidad del contrato tiene tipo_administracion=simple)
  const unidadIds = [
    ...(contrato?.unidad_id ? [contrato.unidad_id] : []),
    ...(contrato?.unidades_contrato || []).map(uc => uc.unidad_id).filter(Boolean),
  ];
  const aplica = unidades.some(u => unidadIds.includes(u.unidad_id) && u.tipo_administracion === 'simple');

  useEffect(() => {
    if (!aplica || !contratoId) { setLoading(false); return; }
    (async () => {
      try { const res = await inquilinosApi.getAcceso(contratoId); setInfo(res.data); }
      catch { toast.error('Error al cargar acceso'); }
      finally { setLoading(false); }
    })();
  }, [contratoId, aplica]);

  if (!aplica) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
        El acceso del inquilino solo aplica para contratos con <strong>administración simple</strong>.
        Cambiá el tipo de administración de la unidad para habilitarlo.
      </div>
    );
  }

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const handleCrear = async () => {
    setActing(true);
    try {
      const payload = {};
      if (newUsername.trim()) payload.username = newUsername.trim();
      if (newPassword.trim()) payload.password = newPassword.trim();
      const res = await inquilinosApi.crearAcceso(contratoId, payload);
      setCredencialReciente({ username: res.data.username, password: res.data.password_provisoria });
      toast.success('Acceso creado. Copiale las credenciales al inquilino.');
      const fresh = await inquilinosApi.getAcceso(contratoId);
      setInfo(fresh.data);
      setNewUsername('');
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear acceso'); }
    finally { setActing(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Resetear la contraseña del inquilino?')) return;
    setActing(true);
    try {
      const res = await inquilinosApi.resetPassword(contratoId);
      setCredencialReciente({ username: res.data.username, password: res.data.password_provisoria });
      toast.success('Contraseña reseteada. Copiala al inquilino antes de cerrar.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al resetear'); }
    finally { setActing(false); }
  };

  const handleDeshabilitar = async () => {
    if (!window.confirm('¿Deshabilitar el acceso del inquilino? Se elimina su usuario.')) return;
    setActing(true);
    try {
      await inquilinosApi.deshabilitar(contratoId);
      toast.success('Acceso deshabilitado');
      setInfo({ aplica: true, tiene_acceso: false, user: null, username: null });
      setCredencialReciente(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setActing(false); }
  };

  if (loading) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      {credencialReciente && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Credenciales generadas — mostrar solo esta vez
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between bg-white border border-amber-200 rounded px-3 py-2">
              <div><span className="text-slate-500 mr-2">Usuario:</span><span className="font-mono">{credencialReciente.username}</span></div>
              <Button size="sm" variant="outline" onClick={() => copy(credencialReciente.username)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="flex items-center justify-between bg-white border border-amber-200 rounded px-3 py-2">
              <div><span className="text-slate-500 mr-2">Contraseña:</span><span className="font-mono">{credencialReciente.password}</span></div>
              <Button size="sm" variant="outline" onClick={() => copy(credencialReciente.password)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <p className="text-xs text-amber-700 mt-2">Compartíselas al inquilino por WhatsApp o email. Después de cerrar este diálogo no vas a poder volver a ver la contraseña.</p>
        </div>
      )}

      {info?.tiene_acceso ? (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-green-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> El inquilino tiene acceso creado</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-slate-500">Usuario:</span>
              <span className="ml-2 font-mono">{info.username || info.user?.email}</span>
            </div>
            <div>
              <span className="text-slate-500">Nombre:</span>
              <span className="ml-2">{info.user?.nombre}</span>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={handleReset} disabled={acting} data-testid="reset-pass-inquilino">
              <RefreshCw className="w-4 h-4 mr-1" /> Resetear contraseña
            </Button>
            <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleDeshabilitar} disabled={acting} data-testid="deshabilitar-inquilino">
              <Trash2 className="w-4 h-4 mr-1" /> Deshabilitar acceso
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
          <p className="text-sm text-slate-600">Este inquilino todavía no tiene acceso al sistema. Crealo para que pueda ver sus expensas.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Usuario (email o CUIT)</Label>
              <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="Se autocompleta con datos del contrato" data-testid="inquilino-username" />
            </div>
            <div>
              <Label>Contraseña (opcional)</Label>
              <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Se genera automáticamente si lo dejás vacío" data-testid="inquilino-password" />
            </div>
          </div>
          <Button onClick={handleCrear} disabled={acting} data-testid="crear-acceso-inquilino">
            {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Crear acceso
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccesoInquilinoSection;
